import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Benutzer } from '@beholder/shared';
import {
  SITZUNG_ART_NAMEN,
  type Idee,
  type IdeenNachricht,
  type IdeenPerson,
  type IdeenSitzung,
  type IdeenZiele,
  type SuchErgebnis,
} from '@beholder/shared/module/ideen';
import { api } from '../../core/api';
import { Auswahl, Dialog, Eingabe, Feld, Knopf, Laden, Leer, Meldung, Textfeld, useAktion } from '../../core/ui';
import { pfad, SEITE, sichtbarkeitText, sortieren, useSitzung, useZiele, type Sortierung } from './daten';
import { IdeeDialog, IdeeKarte, LEER, type IdeeFormular } from './IdeeKarte';
import { Adresse, KiText, uhrzeit, Zeichen } from './Text';

const BEISPIELE_IDEEN = [
  'Was könnten wir noch anbieten, damit mehr Leute kommen?',
  'Wie sparen wir 20 % der Kosten?',
  'Welche Risiken übersehen wir gerade?',
];
const BEISPIELE_SUCHE = ['Was wissen wir schon über die Bühne?', 'Welche Angebote haben wir für Technik?', 'Wo steht etwas zur Genehmigung?'];

export function SitzungAnsicht({
  ich,
  sitzungId,
  schliessen,
  geaendert,
}: {
  ich: Benutzer;
  sitzungId: string;
  /** Unterreiter in der eigenen Leiste schließen */
  schliessen(id: string): void;
  /** Leiste neu laden (Name/Sichtbarkeit geändert) */
  geaendert(): void;
}) {
  const s = useSitzung(sitzungId, ich.id);
  const ziele = useZiele(sitzungId, `${s.sitzung?.projektId ?? ''}`);
  const navigate = useNavigate();
  const [ansicht, setAnsicht] = useState<'gespraech' | 'ideen'>('gespraech');
  const [neuDialog, setNeuDialog] = useState<IdeeFormular | null>(null);
  const [neuQuelle, setNeuQuelle] = useState<'hand' | 'suche'>('hand');
  const [teilen, setTeilen] = useState(false);
  const a = useAktion();

  if (s.weg) {
    return (
      <Leer titel="Diesen Unterreiter gibt es nicht (mehr).">
        Er wurde gelöscht, nicht mehr mit dir geteilt oder die Adresse stimmt nicht. <Link to={SEITE}>Zur Übersicht</Link>
      </Leer>
    );
  }
  if (!s.sitzung) return s.fehler ? <Meldung>{s.fehler}</Meldung> : <Laden />;
  const sitzung = s.sitzung;
  const istSuche = sitzung.art === 'suche';
  const schreibbar = sitzung.meineStufe === 'schreiben';
  const darfLoeschen = sitzung.projektId ? schreibbar : sitzung.erstelltVon?.id === ich.id;

  const loeschen = () => {
    const wohin = sitzung.projektId ? 'in den Papierkorb des Projekts' : 'in deinen Papierkorb (unten auf dieser Seite)';
    if (!confirm(`„${sitzung.titel}“ samt Gespräch und Ideen ${wohin} legen? (Lässt sich 180 Tage lang zurückholen.)`)) return;
    void a.ausfuehren(async () => {
      await api.delete(pfad(`/sitzungen/${sitzung.id}`));
      geaendert();
      navigate(SEITE);
    });
  };

  const alsIdee = (n: IdeenNachricht) => {
    const e = n.suche;
    const quellen = e?.web.quellen.length ? `\n\nQuellen (nur Text):\n${e.web.quellen.map((q) => `[${q.nr}] ${q.titel} – ${q.url}`).join('\n')}` : '';
    const text = (n.text || e?.projekt.treffer.map((t) => `${t.art} „${t.titel}“`).join(', ') || '').slice(0, 4000 - quellen.length);
    setNeuQuelle('suche');
    setNeuDialog({ ...LEER, titel: (e?.frage ?? '').slice(0, 200), beschreibung: `${text}${quellen}`.slice(0, 5000) });
  };

  return (
    <div className="id-sitzung">
      <header className="id-sitzung-kopf">
        <span className="id-art" title={SITZUNG_ART_NAMEN[sitzung.art]}>
          {istSuche ? Zeichen.lupe : Zeichen.stern} {SITZUNG_ART_NAMEN[sitzung.art]}
        </span>
        <span className={`id-sicht id-sicht-${sitzung.sichtbarkeit}`} title="Wer diesen Unterreiter sieht">
          {sitzung.sichtbarkeit === 'projekt' && sitzung.projektId ? (
            <>
              {Zeichen.ordner} Projekt{' '}
              {sitzung.projektLesen ? <Link to={`/p/${sitzung.projektId}`}>{sitzung.projektName}</Link> : `„${sitzung.projektName ?? ''}“`}
              {sitzung.freigaben.length > 0 && ` · geteilt mit ${sitzung.freigaben.map((f) => f.name).join(', ')}`}
              {!sitzung.projektLesen && ' (du bist über eine Freigabe dabei)'}
            </>
          ) : (
            <>
              {sitzung.sichtbarkeit === 'geteilt' ? Zeichen.leute : Zeichen.schloss} {sichtbarkeitText(sitzung, ich.id)}
            </>
          )}
        </span>
        {sitzung.meineStufe === 'lesen' && <span className="id-marke">nur lesen</span>}
        <span className="abstand" />
        {s.live && <Anwesende personen={s.anwesend} ichId={ich.id} />}
        <div className="reihe">
          {sitzung.darfVerwalten && (
            <Knopf klein onClick={() => setTeilen(true)} title="Teilen oder einem Projekt zuordnen">
              {Zeichen.leute} Teilen / Projekt
            </Knopf>
          )}
          <Knopf klein art="leise" onClick={() => schliessen(sitzung.id)} title="Aus deiner Leiste nehmen – bleibt unter „Geschlossene“">
            Schließen
          </Knopf>
          {darfLoeschen && (
            <Knopf klein art="leise" onClick={loeschen} title="In den Papierkorb">
              {Zeichen.x} Löschen
            </Knopf>
          )}
        </div>
      </header>
      <Meldung>{a.fehler}</Meldung>

      <div className="id-umschalter" role="tablist">
        <button type="button" role="tab" aria-selected={ansicht === 'gespraech'} className={ansicht === 'gespraech' ? 'aktiv' : ''} onClick={() => setAnsicht('gespraech')}>
          {istSuche ? 'Suche' : 'Gespräch'}
        </button>
        <button type="button" role="tab" aria-selected={ansicht === 'ideen'} className={ansicht === 'ideen' ? 'aktiv' : ''} onClick={() => setAnsicht('ideen')}>
          Ideen ({s.ideen.length})
        </button>
      </div>

      <div className={`id-raster id-zeige-${ansicht}`}>
        <Gespraech
          nachrichten={s.nachrichten}
          denkt={s.denkt}
          tippend={s.tippend.map((t) => t.name)}
          istSuche={istSuche}
          schreibbar={schreibbar}
          ziele={ziele}
          sitzung={sitzung}
          tippe={s.tippe}
          alsIdee={alsIdee}
          zuIdeen={() => setAnsicht('ideen')}
          gesendet={() => void s.laden()}
        />
        <IdeenSpalte
          ideen={s.ideen}
          ichId={ich.id}
          darfSchreiben={schreibbar}
          ziele={ziele}
          frisch={new Set(s.nachrichten.at(-1)?.ideenIds ?? [])}
          neu={() => {
            setNeuQuelle('hand');
            setNeuDialog(LEER);
          }}
          istSuche={istSuche}
          projektlos={!sitzung.projektId}
        />
      </div>

      <IdeeDialog
        offen={!!neuDialog}
        schliessen={() => setNeuDialog(null)}
        titel={neuQuelle === 'suche' ? 'Als Idee übernehmen' : 'Eigene Idee aufschreiben'}
        start={neuDialog ?? LEER}
        speichern={async (f) => {
          await api.post<Idee>(pfad(`/sitzungen/${sitzung.id}/ideen`), {
            titel: f.titel,
            beschreibung: f.beschreibung,
            kosten: f.kosten,
            kostenEuro: f.kostenZahl,
            aufwand: f.aufwand,
            risiken: f.risiken,
            quelle: neuQuelle,
          });
          setAnsicht('ideen');
          void s.laden();
        }}
      />
      {teilen && (
        <TeilenDialog
          sitzung={sitzung}
          ziele={ziele}
          schliessen={() => setTeilen(false)}
          fertig={() => {
            setTeilen(false);
            void s.laden();
            geaendert();
          }}
        />
      )}
    </div>
  );
}

/**
 * Wer sieht den Unterreiter: geteilt mit ausgewählten Personen (alle aktiven Konten, Suche nach Name) und optional
 * einem Projekt zugeordnet. Freigaben gelten zusätzlich zu den Projektrechten (Marc, 04.11.).
 */
function TeilenDialog({
  sitzung,
  ziele,
  schliessen,
  fertig,
}: {
  sitzung: IdeenSitzung;
  ziele: IdeenZiele | null;
  schliessen(): void;
  fertig(): void;
}) {
  const [mitProjekt, setMitProjekt] = useState(!!sitzung.projektId);
  const [projektId, setProjektId] = useState(sitzung.projektId ?? ziele?.projekte[0]?.id ?? '');
  const [freigaben, setFreigaben] = useState(new Map(sitzung.freigaben.map((f) => [f.benutzerId, f.stufe])));
  const [personen, setPersonen] = useState<IdeenPerson[] | null>(null);
  const [suche, setSuche] = useState('');
  const a = useAktion();
  useEffect(() => {
    api
      .get<IdeenPerson[]>(pfad('/personen'))
      .then((l) => {
        // Bereits Geteilte immer zeigen (falls das Konto z. B. gerade nicht in der Liste ist)
        const da = new Set(l.map((p) => p.id));
        setPersonen([...l, ...sitzung.freigaben.filter((f) => !da.has(f.benutzerId)).map((f) => ({ id: f.benutzerId, name: f.name }))]);
      })
      .catch(() => setPersonen([]));
  }, [sitzung.freigaben]);
  const projekte = [...(ziele?.projekte ?? [])];
  if (sitzung.projektId && !projekte.some((p) => p.id === sitzung.projektId)) projekte.unshift({ id: sitzung.projektId, name: sitzung.projektName ?? '' });
  const eigene = sitzung.erstelltVon?.id;
  const s = suche.trim().toLowerCase();
  // Ausgewählte zuerst, dann nach Suche gefiltert
  const liste = (personen ?? [])
    .filter((p) => p.id !== eigene)
    .filter((p) => freigaben.has(p.id) || !s || p.name.toLowerCase().includes(s))
    .sort((x, y) => Number(freigaben.has(y.id)) - Number(freigaben.has(x.id)) || x.name.localeCompare(y.name));

  const speichern = () =>
    void a.ausfuehren(async () => {
      await api.put(pfad(`/sitzungen/${sitzung.id}/sichtbarkeit`), {
        projektId: mitProjekt ? projektId || null : null,
        freigaben: [...freigaben].map(([benutzerId, stufe]) => ({ benutzerId, stufe })),
      });
      fertig();
    });

  return (
    <Dialog
      titel="Wer sieht diesen Unterreiter?"
      offen
      schliessen={schliessen}
      breit
      fuss={
        <>
          <Knopf art="haupt" disabled={a.laeuft || (mitProjekt && !projektId)} onClick={speichern}>
            Speichern
          </Knopf>
          <Knopf onClick={schliessen}>Abbrechen</Knopf>
        </>
      }
    >
      <div className="stapel">
        <Meldung>{a.fehler}</Meldung>
        <label className="id-wahl">
          <input type="checkbox" checked={mitProjekt} onChange={(e) => setMitProjekt(e.target.checked)} disabled={!projekte.length} />
          <span>
            <strong>Einem Projekt zuordnen</strong> – dann sehen ihn auch alle mit Recht auf „Ideen“ im Projekt, die KI kennt das Projekt, und
            Ideen lassen sich als Aufgabe, Mindmap-Punkt oder Kostenvorschlag übernehmen.
          </span>
        </label>
        {mitProjekt && (
          <Feld titel="Projekt">
            <Auswahl value={projektId} onChange={(e) => setProjektId(e.target.value)}>
              {projekte.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Auswahl>
          </Feld>
        )}
        {!projekte.length && <p className="leise klein">Du hast kein Projekt, in dem du Ideen anlegen darfst.</p>}

        <Feld
          titel={`Geteilt mit${freigaben.size ? ` (${freigaben.size})` : ''}`}
          hinweis={
            mitProjekt
              ? 'Gilt zusätzlich zu den Projektrechten. Wer nicht im Projekt ist, sieht den Unterreiter, aber kein Projektwissen.'
              : 'Ohne Auswahl siehst nur du den Unterreiter.'
          }
        >
          <Eingabe value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Name suchen …" aria-label="Person suchen" />
        </Feld>
        <div className="id-personen">
          {!personen ? (
            <Laden />
          ) : liste.length === 0 ? (
            <p className="leise klein">{s ? 'Niemand mit diesem Namen.' : 'Keine weiteren Konten.'}</p>
          ) : (
            liste.slice(0, 60).map((p) => {
              const st = freigaben.get(p.id);
              return (
                <div key={p.id} className="id-person">
                  <label>
                    <input
                      type="checkbox"
                      checked={!!st}
                      onChange={(e) =>
                        setFreigaben((m) => {
                          const n = new Map(m);
                          if (e.target.checked) n.set(p.id, 'schreiben');
                          else n.delete(p.id);
                          return n;
                        })
                      }
                    />{' '}
                    {p.name}
                  </label>
                  {st && (
                    <Auswahl
                      className="id-klein-auswahl"
                      value={st}
                      aria-label={`Recht für ${p.name}`}
                      onChange={(e) => setFreigaben((m) => new Map(m).set(p.id, e.target.value as 'lesen' | 'schreiben'))}
                    >
                      <option value="schreiben">darf mitschreiben</option>
                      <option value="lesen">darf nur lesen</option>
                    </Auswahl>
                  )}
                </div>
              );
            })
          )}
          {liste.length > 60 && <p className="leise klein">… weitere – bitte oben nach dem Namen suchen.</p>}
        </div>
      </div>
    </Dialog>
  );
}

function Anwesende({ personen, ichId }: { personen: { id: string; name: string }[]; ichId: string }) {
  const andere = personen.filter((p) => p.id !== ichId);
  if (!andere.length) return null;
  return (
    <span className="id-anwesend" title={`Gerade hier: ${andere.map((p) => p.name).join(', ')}`}>
      {andere.slice(0, 5).map((p) => (
        <span key={p.id} className="id-kopf-kreis" style={{ background: farbe(p.id) }}>
          {p.name.slice(0, 1).toUpperCase()}
        </span>
      ))}
      <span className="leise klein">{andere.length === 1 ? `${andere[0]!.name} ist hier` : `${andere.length} weitere hier`}</span>
    </span>
  );
}

function farbe(id: string): string {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) % 360;
  return `hsl(${h} 55% 45%)`;
}

function Gespraech({
  nachrichten,
  denkt,
  tippend,
  istSuche,
  schreibbar,
  ziele,
  sitzung,
  tippe,
  alsIdee,
  zuIdeen,
  gesendet,
}: {
  nachrichten: IdeenNachricht[];
  denkt: { von: string; was: string } | null;
  tippend: string[];
  istSuche: boolean;
  schreibbar: boolean;
  ziele: IdeenZiele | null;
  sitzung: IdeenSitzung;
  tippe(): void;
  alsIdee(n: IdeenNachricht): void;
  zuIdeen(): void;
  /** nach dem Senden gleich nachladen (ohne Live) */
  gesendet(): void;
}) {
  const [text, setText] = useState('');
  const [imProjekt, setImProjekt] = useState(true);
  const [imInternet, setImInternet] = useState(false);
  const a = useAktion();
  const liste = useRef<HTMLDivElement>(null);
  const feld = useRef<HTMLTextAreaElement>(null);
  const web = ziele?.websuche;

  useEffect(() => {
    const el = liste.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [nachrichten.length, denkt]);

  const senden = async () => {
    const t = text.trim();
    if (!t || denkt) return;
    const ok = await a.ausfuehren(() =>
      api.post(pfad(`/sitzungen/${sitzung.id}/nachrichten`), { text: t, imProjekt, imInternet: imInternet && !!web?.ja }),
    );
    if (ok) {
      setText('');
      gesendet();
    }
  };

  const kiAus = !istSuche && ziele && !ziele.ki;

  // Das Gespräch füllt den Rest des Fensters, damit nur der Verlauf scrollt (nicht die ganze Seite)
  const rahmen = useRef<HTMLElement>(null);
  useEffect(() => {
    const anpassen = () => {
      const el = rahmen.current;
      if (!el || !el.offsetParent) return;
      const oben = el.getBoundingClientRect().top + window.scrollY;
      el.style.height = `${Math.min(900, Math.max(360, window.innerHeight - oben - 16))}px`;
    };
    anpassen();
    window.addEventListener('resize', anpassen);
    return () => window.removeEventListener('resize', anpassen);
  }, []);

  return (
    <section className="id-gespraech" aria-label={istSuche ? 'Suche' : 'Gespräch'} ref={rahmen}>
      <div className="id-verlauf" ref={liste}>
        {nachrichten.length === 0 && (
          <div className="id-leer-gespraech">
            {istSuche ? (
              <>
                <strong>Frag etwas – Beholder sucht im Projekt und auf Wunsch im Internet.</strong>
                <p className="leise">
                  {sitzung.projektId ? `Im Projekt „${sitzung.projektName}“` : 'In allen deinen Projekten'} werden Mindmap, Aufgaben, Budget, Mails,
                  Tafeln, Ideen und Dateitexte durchsucht – nur, was du sehen darfst.
                  Fundstellen aus dem Internet erscheinen nur als Text zum Kopieren; Beholder öffnet keine Adressen.
                </p>
              </>
            ) : (
              <>
                <strong>Sammelt hier gemeinsam Ideen mit der KI.</strong>
                <p className="leise">
                  {sitzung.projektId
                    ? 'Die KI kennt das Projekt (nur, was du sehen darfst) und schlägt Ideen'
                    : 'Noch ohne Projekt: die KI schlägt frei Ideen'}{' '}
                  mit grober Schätzung für Kosten, Aufwand und Risiken vor.
                  Die Ideen erscheinen als Karten – festhalten, bewerten, übernehmen.
                </p>
              </>
            )}
            {schreibbar && (
              <div className="id-beispiele">
                {(istSuche ? BEISPIELE_SUCHE : BEISPIELE_IDEEN).map((b) => (
                  <button key={b} type="button" onClick={() => (setText(b), feld.current?.focus())}>
                    {b}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {nachrichten.map((n) => (
          <Nachricht key={n.id} n={n} darfSchreiben={schreibbar} alsIdee={alsIdee} zuIdeen={zuIdeen} />
        ))}
        {denkt && (
          <div className="id-denkt" aria-live="polite">
            <span className="kreisel" aria-hidden /> Die KI {denkt.was} … <span className="leise klein">(für {denkt.von})</span>
          </div>
        )}
      </div>
      <div className="id-tippt" aria-live="polite">
        {tippend.length > 0 && `${tippend.join(', ')} ${tippend.length === 1 ? 'tippt' : 'tippen'} gerade …`}
      </div>
      {schreibbar ? (
        <form
          className="id-eingabe"
          onSubmit={(e) => {
            e.preventDefault();
            void senden();
          }}
        >
          <Meldung>{a.fehler}</Meldung>
          {kiAus && <Meldung art="info">Die KI ist in diesem Projekt ausgeschaltet. Eigene Ideen kannst du rechts trotzdem aufschreiben.</Meldung>}
          {istSuche && (
            <div className="id-wo">
              <label>
                <input type="checkbox" checked={imProjekt} onChange={(e) => setImProjekt(e.target.checked)} />{' '}
                {sitzung.projektId ? `im Projekt „${sitzung.projektName}“` : 'in meinen Projekten'}
              </label>
              <label title={web && !web.ja ? web.grund : 'Recherche über die KI-Hilfe; Quellen nur als Text'}>
                <input type="checkbox" checked={imInternet && !!web?.ja} disabled={!web?.ja} onChange={(e) => setImInternet(e.target.checked)} /> im Internet
              </label>
              {web && !web.ja && <span className="leise klein">{web.grund}</span>}
            </div>
          )}
          <div className="id-eingabe-zeile">
            <Textfeld
              ref={feld}
              value={text}
              rows={2}
              maxLength={4000}
              placeholder={istSuche ? 'Wonach suchst du?' : 'Frag nach Ideen oder sag, worum es geht …'}
              onChange={(e) => {
                setText(e.target.value);
                if (e.target.value) tippe();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  void senden();
                }
              }}
              disabled={!!kiAus}
            />
            <Knopf
              art="haupt"
              type="submit"
              disabled={!text.trim() || !!denkt || a.laeuft || !!kiAus || (istSuche && !imProjekt && !(imInternet && web?.ja))}
              title="Senden (Enter)"
            >
              {istSuche ? Zeichen.lupe : Zeichen.senden} <span className="id-knopf-text">{istSuche ? 'Suchen' : 'Senden'}</span>
            </Knopf>
          </div>
        </form>
      ) : (
        <p className="leise klein id-nur-lesen">
          Du kannst hier nur mitlesen.
        </p>
      )}
    </section>
  );
}

function Nachricht({
  n,
  darfSchreiben,
  alsIdee,
  zuIdeen,
}: {
  n: IdeenNachricht;
  darfSchreiben: boolean;
  alsIdee(n: IdeenNachricht): void;
  zuIdeen(): void;
}) {
  if (n.rolle === 'hinweis') return <div className="id-hinweis">{n.text}</div>;
  if (n.rolle === 'user') {
    return (
      <div className="id-nachricht id-von-mensch">
        <div className="id-nachricht-kopf">
          <strong>{n.von?.name ?? 'Jemand'}</strong> <span className="leise klein">{uhrzeit(n.erstelltAm)}</span>
        </div>
        <div className="id-blase">{n.text}</div>
      </div>
    );
  }
  return (
    <div className="id-nachricht id-von-ki">
      <div className="id-nachricht-kopf">
        <strong>KI</strong> <span className="leise klein">{uhrzeit(n.erstelltAm)}{n.anbieter === 'claude' ? ' · Claude' : n.anbieter === 'lokal' ? ' · lokale KI' : ''}</span>
      </div>
      <div className="id-blase">
        {n.suche ? <SuchAnzeige e={n.suche} /> : <KiText text={n.text} />}
        {n.ideenIds.length > 0 && (
          <button type="button" className="id-link-knopf" onClick={zuIdeen}>
            {n.ideenIds.length === 1 ? '1 neue Idee' : `${n.ideenIds.length} neue Ideen`} bei den Karten →
          </button>
        )}
        {n.suche && darfSchreiben && (n.text || n.suche.projekt.treffer.length > 0) && (
          <div className="id-aktionen">
            <Knopf klein onClick={() => alsIdee(n)}>
              Als Idee übernehmen
            </Knopf>
          </div>
        )}
      </div>
    </div>
  );
}

function SuchAnzeige({ e }: { e: SuchErgebnis }) {
  return (
    <div className="id-suche">
      {e.projekt.gesucht && (
        <div className="id-suche-teil">
          <h4>{e.projekt.wo ? e.projekt.wo.replace(/^./, (c) => c.toUpperCase()) : 'Im Projekt'}</h4>
          {e.projekt.zusammenfassung && <KiText text={e.projekt.zusammenfassung} />}
          {e.projekt.hinweis && <p className="leise klein">{e.projekt.hinweis}</p>}
          {e.projekt.treffer.length > 0 && (
            <ol className="id-treffer">
              {e.projekt.treffer.map((t) => (
                <li key={t.nr}>
                  <span className="id-nr">{t.nr}</span>
                  <div>
                    <Link to={t.pfad}>{t.titel}</Link>{' '}
                    <span className="leise klein">
                      · {t.art}
                      {t.projektName ? ` · ${t.projektName}` : ''}
                    </span>
                    {t.auszug && <div className="klein leise id-auszug">{t.auszug}</div>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
      {e.web.gesucht && (
        <div className="id-suche-teil">
          <h4>Im Internet</h4>
          {e.web.antwort && <KiText text={e.web.antwort} />}
          {e.web.hinweis && <p className="leise klein">{e.web.hinweis}</p>}
          {e.web.quellen.length > 0 && (
            <>
              <p className="klein leise">Quellen – nur als Text, Beholder öffnet keine Adressen:</p>
              <ol className="id-treffer">
                {e.web.quellen.map((q) => (
                  <li key={q.nr}>
                    <span className="id-nr">{q.nr}</span>
                    <div>
                      <strong>{q.titel || 'Ohne Titel'}</strong>
                      <Adresse url={q.url} />
                      {q.auszug && <div className="klein leise id-auszug">{q.auszug}</div>}
                    </div>
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function IdeenSpalte({
  ideen,
  ichId,
  darfSchreiben,
  ziele,
  frisch,
  neu,
  istSuche,
  projektlos,
}: {
  ideen: Idee[];
  ichId: string;
  darfSchreiben: boolean;
  ziele: IdeenZiele | null;
  frisch: Set<string>;
  neu(): void;
  istSuche: boolean;
  projektlos: boolean;
}) {
  const [sortierung, setSortierung] = useState<Sortierung>('stimmen');
  const [verworfene, setVerworfene] = useState(false);
  const sichtbar = useMemo(() => sortieren(ideen.filter((i) => verworfene || i.status !== 'verworfen'), sortierung), [ideen, verworfene, sortierung]);
  const anzahlVerworfen = ideen.filter((i) => i.status === 'verworfen').length;
  return (
    <aside className="id-ideen" aria-label="Ideen">
      <div className="id-ideen-kopf">
        <h3>Ideen</h3>
        <Auswahl value={sortierung} onChange={(e) => setSortierung(e.target.value as Sortierung)} aria-label="Sortieren" className="id-klein-auswahl">
          <option value="stimmen">nach Stimmen</option>
          <option value="neu">neueste zuerst</option>
          <option value="alt">älteste zuerst</option>
        </Auswahl>
        {darfSchreiben && (
          <Knopf klein onClick={neu}>
            + Eigene Idee
          </Knopf>
        )}
      </div>
      {anzahlVerworfen > 0 && (
        <label className="klein leise id-schalter">
          <input type="checkbox" checked={verworfene} onChange={(e) => setVerworfene(e.target.checked)} /> verworfene zeigen ({anzahlVerworfen})
        </label>
      )}
      {projektlos && ideen.length > 0 && darfSchreiben && (
        <p className="leise klein">
          Übernehmen als Aufgabe, Mindmap-Punkt oder Kostenvorschlag geht, sobald der Unterreiter einem Projekt zugeordnet ist („Teilen / Projekt“). Eine
          Idee lässt sich aber jederzeit in ein Projekt übernehmen oder daraus ein neues Projekt anlegen (Menü ⋯).
        </p>
      )}
      {sichtbar.length === 0 ? (
        <p className="leise klein">
          {ideen.length
            ? 'Alle Ideen sind verworfen.'
            : istSuche
              ? 'Noch keine Ideen. Unter einem Suchergebnis: „Als Idee übernehmen“.'
              : 'Noch keine Ideen. Schreib der KI links, worum es geht – oder trag eine eigene Idee ein.'}
        </p>
      ) : (
        <div className="id-karten">
          {sichtbar.map((i) => (
            <IdeeKarte key={i.id} idee={i} ichId={ichId} darfSchreiben={darfSchreiben} ziele={ziele} neu={frisch.has(i.id)} />
          ))}
        </div>
      )}
    </aside>
  );
}
